// Tiny DOM helpers. Everything is built with createElement and text nodes, never innerHTML with data, so text from
// the backends (titles, reviews, names) can't inject markup.

import { ApiError, needsSignIn } from "./api.ts";

type Child = Node | string | number | null | undefined | false | Child[];
type Attrs = Record<string, string | number | boolean | null | undefined | EventListener | Partial<CSSStyleDeclaration>>;

export function h<K extends keyof HTMLElementTagNameMap>(tag: K, attrs: Attrs | null = null, ...children: Child[]): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  if (attrs) {
    for (const [k, v] of Object.entries(attrs)) {
      if (v === null || v === undefined || v === false) continue;
      if (k.startsWith("on") && typeof v === "function") el.addEventListener(k.slice(2).toLowerCase(), v as EventListener);
      else if (k === "style" && typeof v === "object") Object.assign(el.style, v);
      else if (k === "class") el.className = String(v);
      else if (v === true) el.setAttribute(k, "");
      else el.setAttribute(k, String(v));
    }
  }
  append(el, children);
  return el;
}

export function append(el: Node, children: Child[]): void {
  for (const c of children) {
    if (c === null || c === undefined || c === false) continue;
    if (Array.isArray(c)) append(el, c);
    else el.appendChild(typeof c === "string" || typeof c === "number" ? document.createTextNode(String(c)) : c);
  }
}

export function clear(el: Element, ...children: Child[]): void {
  el.replaceChildren();
  append(el, children);
}

/** An internal link handled by the router. */
export function link(href: string, attrs: Attrs | null, ...children: Child[]): HTMLAnchorElement {
  return h("a", { ...attrs, href, "data-link": true }, ...children);
}

export function toast(message: string): void {
  const t = document.getElementById("toast");
  if (!t) return;
  t.textContent = message;
  t.classList.add("on");
  clearTimeout((t as unknown as { _timer?: number })._timer);
  (t as unknown as { _timer?: number })._timer = window.setTimeout(() => t.classList.remove("on"), 3500);
}

/** A block that shows "Loading…", then whatever `fill` renders, or the error. */
export function section(title: string | null, fill: (body: HTMLElement) => Promise<void>, attrs: Attrs = {}): HTMLElement {
  const body = h("div", { class: "section-body" }, h("p", { class: "dim" }, "Loading…"));
  const el = h("section", { class: "section", ...attrs }, title ? h("h2", null, title) : null, body);
  fill(body).catch((e) => clear(body, errorBox(e)));
  return el;
}

export function errorBox(e: unknown): HTMLElement {
  const msg = e instanceof Error ? e.message : String(e);
  const box = h("p", { class: "error", role: "alert" }, msg);
  if (needsSignIn(e)) {
    const next = encodeURIComponent(location.pathname + location.search);
    append(box, [" ", h("a", { href: `/signin?next=${next}` }, "Sign in")]);
  } else if (e instanceof ApiError && e.code === "accounts_unreachable") {
    append(box, [" ", h("a", { href: location.pathname + location.search }, "Try again")]);
  }
  return box;
}

export function fmtDate(unixSeconds: number): string {
  return new Date(unixSeconds * 1000).toISOString().slice(0, 10);
}

/** 5400 → "1.5 h", 600 → "10 min". */
export function fmtPlaytime(seconds: number): string {
  if (seconds < 3600) return `${Math.max(1, Math.round(seconds / 60))} min`;
  const hours = seconds / 3600;
  return `${hours < 10 ? hours.toFixed(1).replace(/\.0$/, "") : Math.round(hours)} h`;
}
